import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { transcribeAudio } from "../Api/copilotApi";
import { handleRole } from "../Helper/handleRoleHelper";

/**
 * Voice dictation: MediaRecorder → backend → OpenAI transcription.
 *
 * Design notes:
 * - Live mic level is exposed via getLevel() (a ref read), NOT state — a
 *   waveform reading it in its own rAF avoids 30fps re-renders of the host.
 * - Safari records audio as mp4/aac; Chrome/Firefox as webm/opus. We pick the
 *   first supported mimeType and tell the backend the real container.
 * - Everything is torn down on unmount: tracks, AudioContext, timers, and the
 *   in-flight transcription fetch (AbortController).
 *
 * @param {{ onTranscript: (text: string) => void,
 *           onError?: (message: string) => void,
 *           maxSeconds?: number }} params
 */
const MIME_CANDIDATES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
];

export default function useVoiceDictation({
  onTranscript,
  onError,
  maxSeconds = 90,
}) {
  const loginToken = useSelector((s) => s?.reducer?.userToken);
  const role = useSelector((s) => s?.reducer?.userLoggedIn?.role);

  const [status, setStatus] = useState("idle"); // idle | recording | transcribing
  const [elapsed, setElapsed] = useState(0);

  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const levelRef = useRef(0);
  const rafRef = useRef(null);
  const chunksRef = useRef([]);
  const timersRef = useRef({ tick: null, cap: null });
  const abortRef = useRef(null);
  const discardRef = useRef(false);
  const mimeRef = useRef("audio/webm");
  // Synchronous guard: recorderRef isn't set until AFTER the getUserMedia
  // await resolves, so without this a second start() (rapid toggle, Alt+M
  // key-repeat) during the permission prompt would orphan the first stream.
  const startingRef = useRef(false);
  const mountedRef = useRef(true);
  // Silence auto-stop (VAD-lite): stop ~2s after speech ends; if nothing is
  // ever said, discard after a few seconds so the mic doesn't hang open.
  const lastLoudRef = useRef(0);
  const hasSpokenRef = useRef(false);
  const startedAtRef = useRef(0);
  const autoEndedRef = useRef(false);
  const SPEECH_LEVEL = 0.1;
  const SILENCE_STOP_MS = 3000;
  const NO_SPEECH_MS = 6000;
  // Kept in refs so start/stop stay stable across renders.
  const onTranscriptRef = useRef(onTranscript);
  const onErrorRef = useRef(onError);
  onTranscriptRef.current = onTranscript;
  onErrorRef.current = onError;

  const isSupported =
    typeof window !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof window.MediaRecorder !== "undefined";

  const teardownCapture = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (timersRef.current.tick) clearInterval(timersRef.current.tick);
    if (timersRef.current.cap) clearTimeout(timersRef.current.cap);
    timersRef.current = { tick: null, cap: null };
    try {
      streamRef.current?.getTracks().forEach((t) => t.stop());
    } catch {
      /* already stopped */
    }
    streamRef.current = null;
    try {
      audioCtxRef.current?.close();
    } catch {
      /* already closed */
    }
    audioCtxRef.current = null;
    analyserRef.current = null;
    levelRef.current = 0;
    recorderRef.current = null;
  }, []);

  const fail = useCallback(
    (message) => {
      teardownCapture();
      setStatus("idle");
      setElapsed(0);
      onErrorRef.current?.(message);
    },
    [teardownCapture]
  );

  /** Current mic level 0..1 — read this inside your own rAF for waveforms. */
  const getLevel = useCallback(() => levelRef.current, []);
  // 0→1 as the post-speech silence approaches the auto-send point — lets the
  // meter visibly "close out" the turn so auto-send never feels abrupt.
  const getSilenceProgress = useCallback(() => {
    if (!hasSpokenRef.current || !lastLoudRef.current) return 0;
    return Math.min(1, (performance.now() - lastLoudRef.current) / SILENCE_STOP_MS);
  }, []);

  const stop = useCallback(() => {
    const rec = recorderRef.current;
    if (!rec || rec.state === "inactive") return;
    discardRef.current = false;
    try {
      rec.stop(); // onstop handles transcription
    } catch {
      fail("Recording could not be stopped.");
    }
  }, [fail]);

  /** Discard the recording without transcribing (Escape / dismiss). */
  const cancel = useCallback(() => {
    const rec = recorderRef.current;
    discardRef.current = true;
    if (rec && rec.state !== "inactive") {
      try {
        rec.stop();
      } catch {
        /* noop */
      }
    }
    // Also abort an in-flight transcription fetch (cancel during "transcribing").
    try {
      abortRef.current?.abort();
    } catch {
      /* noop */
    }
    teardownCapture();
    setStatus("idle");
    setElapsed(0);
  }, [teardownCapture]);

  const start = useCallback(async () => {
    if (!isSupported) {
      onErrorRef.current?.("Voice input isn't supported in this browser.");
      return;
    }
    if (recorderRef.current || startingRef.current) return; // already (pre)recording
    startingRef.current = true;
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });
    } catch (err) {
      startingRef.current = false;
      const denied =
        err?.name === "NotAllowedError" || err?.name === "SecurityError";
      onErrorRef.current?.(
        denied
          ? "Microphone access was blocked. Allow it in your browser's site settings."
          : "Couldn't access the microphone."
      );
      return;
    }
    // The component may have unmounted (or cancel() run) while the permission
    // prompt was up — don't light up a mic nobody can turn off.
    if (!mountedRef.current || discardRef.current) {
      stream.getTracks().forEach((t) => t.stop());
      startingRef.current = false;
      return;
    }
    streamRef.current = stream;

    const mime =
      MIME_CANDIDATES.find(
        (m) =>
          typeof MediaRecorder.isTypeSupported === "function" &&
          MediaRecorder.isTypeSupported(m)
      ) || "";
    mimeRef.current = mime || "audio/webm";

    let rec;
    try {
      rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
    } catch {
      teardownCapture();
      startingRef.current = false;
      onErrorRef.current?.("Couldn't start the recorder.");
      return;
    }
    recorderRef.current = rec;
    chunksRef.current = [];
    discardRef.current = false;
    autoEndedRef.current = false;
    hasSpokenRef.current = false;
    startedAtRef.current = performance.now();
    lastLoudRef.current = performance.now();

    // Live level meter (functional feedback, read via getLevel()).
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      // Safari can create the context 'suspended' outside the gesture stack →
      // flat readings; resume so the level meter actually moves.
      if (ctx.state === "suspended" && typeof ctx.resume === "function") {
        ctx.resume().catch(() => {});
      }
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      src.connect(analyser);
      audioCtxRef.current = ctx;
      analyserRef.current = analyser;
      const data = new Uint8Array(analyser.frequencyBinCount);
      const loop = () => {
        if (!analyserRef.current) return;
        analyserRef.current.getByteTimeDomainData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i += 1) {
          const v = (data[i] - 128) / 128;
          sum += v * v;
        }
        // RMS with a little gain so normal speech reads ~0.3-0.8.
        const level = Math.min(1, Math.sqrt(sum / data.length) * 3.2);
        levelRef.current = level;

        // Silence-based auto-end (once).
        const now = performance.now();
        if (level >= SPEECH_LEVEL) {
          lastLoudRef.current = now;
          hasSpokenRef.current = true;
        }
        if (!autoEndedRef.current && recorderRef.current) {
          if (hasSpokenRef.current) {
            if (now - lastLoudRef.current > SILENCE_STOP_MS) {
              autoEndedRef.current = true;
              stop(); // they finished talking → transcribe what we have
              return;
            }
          } else if (now - startedAtRef.current > NO_SPEECH_MS) {
            autoEndedRef.current = true;
            cancel(); // never spoke → drop, but say so (not a silent dead-end)
            onErrorRef.current?.(
              "Didn't catch that — tap the mic or press Alt+M to talk."
            );
            return;
          }
        }
        rafRef.current = requestAnimationFrame(loop);
      };
      rafRef.current = requestAnimationFrame(loop);
    } catch {
      /* level meter is optional — recording still works */
    }

    rec.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };
    rec.onerror = () => fail("Recording failed.");
    rec.onstop = async () => {
      const wasDiscarded = discardRef.current;
      const chunks = chunksRef.current;
      chunksRef.current = [];
      teardownCapture();
      if (wasDiscarded) return;
      const blob = new Blob(chunks, { type: mimeRef.current });
      if (blob.size < 2000) {
        // <~0.1s of audio: treat as an accidental tap, not an error worth a toast.
        setStatus("idle");
        setElapsed(0);
        return;
      }
      setStatus("transcribing");
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const ext = mimeRef.current.includes("mp4") ? "mp4" : "webm";
        const { text } = await transcribeAudio({
          token: loginToken,
          role: handleRole(role),
          blob,
          filename: `dictation.${ext}`,
          signal: controller.signal,
        });
        setStatus("idle");
        setElapsed(0);
        if (String(text || "").trim()) {
          onTranscriptRef.current?.(String(text).trim());
        } else {
          onErrorRef.current?.("Couldn't hear anything — try again closer to the mic.");
        }
      } catch (err) {
        if (err?.name === "AbortError") return;
        setStatus("idle");
        setElapsed(0);
        onErrorRef.current?.(err?.message || "Transcription failed.");
      } finally {
        abortRef.current = null;
      }
    };

    try {
      rec.start(250); // gather chunks steadily; avoids one giant final flush
    } catch {
      teardownCapture();
      startingRef.current = false;
      onErrorRef.current?.("Couldn't start the recorder.");
      return;
    }
    startingRef.current = false;
    setElapsed(0);
    setStatus("recording");
    timersRef.current.tick = setInterval(
      () => setElapsed((s) => s + 1),
      1000
    );
    timersRef.current.cap = setTimeout(() => stop(), maxSeconds * 1000);
  }, [isSupported, loginToken, role, maxSeconds, stop, cancel, fail, teardownCapture]);

  const toggle = useCallback(() => {
    if (status === "recording") stop();
    else if (status === "idle") start();
  }, [status, start, stop]);

  // Unmount: kill everything, including an in-flight transcription.
  useEffect(
    () => () => {
      mountedRef.current = false;
      discardRef.current = true;
      try {
        recorderRef.current?.state !== "inactive" && recorderRef.current?.stop();
      } catch {
        /* noop */
      }
      abortRef.current?.abort();
      teardownCapture();
    },
    [teardownCapture]
  );

  return {
    status,
    elapsed,
    isSupported,
    start,
    stop,
    cancel,
    toggle,
    getLevel,
    getSilenceProgress,
  };
}
